import { useContext, useState, ChangeEventHandler } from "react";
import { useForm } from "react-hook-form";
import useAppNavigate from "../router/use-app-navigate";
import CustomerApi from "../../api/customer";
import UploadApi from "../../api/upload";
import AuthContext from "../auth-context";
import Filebox from "../inputs/filebox";
import Form from "../inputs/form";
import Submit from "../inputs/submit";
import Skip from "../inputs/skip";
import { useLocation, useSearchParams } from "react-router-dom";
import PurchaseTicketApi from "../../api/purchase-ticket";
import { useTranslation } from "react-i18next";

interface UploadForm {
  imageId: string;
}

interface UploadImageProps {
  isShop: boolean;
}

export default function UploadImage({ isShop }: UploadImageProps) {
  const context = useContext(AuthContext);
  const { t } = useTranslation(undefined, {
    keyPrefix: isShop ? "Shop" : "SignUp",
  });

  const { handleSubmit } = useForm<UploadForm>();
  const navigate = useAppNavigate();
  const [fileId, setFileId] = useState<number | undefined>();
  const [imagePreview, setImagePreview] = useState<string | undefined>();
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [isSubmit, setIsSubmit] = useState<boolean>(false);
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const { userData } = context;

  const purchaseHandler = () => {
    const productId = searchParams.get("productId");
    if (!context.customerData) {
      const saved = localStorage.getItem("customerData");
      if (saved) {
        try {
          const customerData = JSON.parse(saved);
          context.setCustomerData(customerData);
        } catch (e) {
          console.error("خطا در parse کردن customerData از localStorage:", e);
        }
      }
    }

    const finalProductId = productId || "";
    if (finalProductId && context.customerData) {
      new PurchaseTicketApi(context)
        .post(context.activeFileId!, +finalProductId, context.customerData.type)
        .then((ticket) => {
          if (ticket.errorMessage) {
            navigate(`/signup/purchase`);
          } else {
            new PurchaseTicketApi(context)
              .getAll()
              .then(context.setTickets)
              .catch(() => {});
            navigate(`/signup/payment/${ticket.id}`);
          }
        })
        .catch(() => navigate(`/signup/purchase`));
    } else {
      navigate(`/signup/purchase`);
    }
  };

  const onSubmit = async () => {
    if (!fileId) {
      setErrorMessage("هیچ فایلی آپلود نشده، ارسال نمی‌شود");
      return;
    }
    setIsSubmit(true);
    const { errorMessage } = await new CustomerApi(context).put({
      nationalCardImageId: fileId,
    });
    setIsSubmit(false);
    if (errorMessage) {
      setErrorMessage(errorMessage);
      return;
    }

    if (isShop) {
      navigate(`/`);
    } else if (location.pathname === "/signup/ngn/uploadImage") {
      navigate(`/signup/ngn/selectLine/`);
    } else {
      purchaseHandler();
    }

    return errorMessage;
  };

  const onFileChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    if (event.target.files) {
      const file = event.target.files[0];
      if (file) {
        const fileSizeMB = file.size / (1024 * 1024);
        if (fileSizeMB > 2) {
          setErrorMessage(t("File size exceeds 2 megabytes"));
          return;
        }

        setErrorMessage("");
        setLoading(true);

        const uploadApi = new UploadApi(context);
        uploadApi.post(file).then((id) => {
          setFileId(id);
          const previewURL = URL.createObjectURL(file);
          setImagePreview(previewURL);
          setLoading(false);
        });
      }
    }
  };

  const handleSkip = () => {
    if (location.pathname === "/signup/ngn/uploadImage") {
      navigate(`/signup/ngn/selectLine/`);
    } else {
      purchaseHandler();
    }
  };

  return (
    <Form submit={onSubmit} handleSubmit={handleSubmit}>
      <div className="flex flex-wrap md:flex-no-wrap justify-center pt-8">
        <Filebox name="file" onChange={onFileChange} label="تصویر کارت ملی:" />
        {errorMessage && <div className="text-red-500">{errorMessage}</div>}
        {loading ? (
          <div>{t("Loading")}...</div>
        ) : (
          imagePreview && (
            <img
              src={imagePreview}
              alt="Preview"
              className="w-full h-auto max-w-xs max-h-xs pt-4 md:pt-8"
            />
          )
        )}
      </div>
      <Submit loading={isSubmit} />
      {userData?.isSupportUser && <Skip onClick={handleSkip} />}
    </Form>
  );
}
